/**
 * Button Translator
 * -----------------
 * Globally translates dynamic text that Weglot does not catch automatically:
 *   - .tt-btn-addtocart span:last-child       button text
 *   - .tt-label-sale                           sale label
 *   - .tt-add-info li a                        category label
 *   - .tt-title.prod-thumb-title-color a       product title
 *   - .tt-btn-quickview[data-tooltip]           quick view tooltip
 *
 * Handles:
 *   1. Language switch      → translate or restore all elements on page
 *   2. Swatch/variant click → MutationObserver detects new span
 *                            + 80ms click fallback
 *   3. Infinite scroll      → listingpageajax event
 */

'use strict';

const BTN_SELECTOR   = '.tt-btn-addtocart span:last-child';
const LABEL_SELECTOR = '.tt-label-sale';
const INFO_SELECTOR  = '.tt-add-info li a';
const TITLE_SELECTOR   = '.tt-title.prod-thumb-title-color a';
const TOOLTIP_SELECTOR = '.tt-btn-quickview[data-tooltip]';
const LOCATION_SPAN_SELECTOR = '.tt-label-location > span:not(.tt-label-sale)';
const SWATCH_SELECTOR = '.tt-options-swatch li a';

let btnSourceLang = 'sr';

// =============================================================================
// Helpers
// =============================================================================

function btnBaseLang(lang) {
  return lang ? lang.split('-')[0].toLowerCase() : '';
}

function btnIsSource(lang) {
  return btnBaseLang(lang) === btnBaseLang(btnSourceLang);
}

function btnFix(text) {
  return text.replace(/\bCCP\b/g, 'KKP');
}

function btnTranslate(words, lang, callback) {
  if (typeof Weglot === 'undefined' || !lang || words.length === 0) return;
  Weglot.translate({ words, languageTo: lang }, callback);
}

// =============================================================================
// Sale label helpers
// .tt-label-sale contains a text node ("Popust ") and <span class="thumb_percent">
// Only the text node is touched — the percent span is never modified.
// =============================================================================

function labelGetTextNode(el) {
  for (const node of el.childNodes) {
    if (node.nodeType === 3 && node.textContent.trim()) return node;
  }
  return null;
}

function labelGetText(el) {
  return labelGetTextNode(el)?.textContent.trim() ?? '';
}

function labelSetText(el, text) {
  const node = labelGetTextNode(el);
  if (node) node.textContent = text + ' ';
}

// =============================================================================
// Generic innerText elements (buttons, info, titles)
// =============================================================================

function saveOriginal(el, attr) {
  if (!el.hasAttribute(attr)) {
    const text = el.innerText.trim();
    if (text) el.setAttribute(attr, text);
  }
}

function translateEls(selector, attr, lang) {
  document.querySelectorAll(selector).forEach(el => saveOriginal(el, attr));

  const els   = Array.from(document.querySelectorAll(selector)).filter(el => el.hasAttribute(attr));
  const words = els.map(el => ({ t: 1, w: el.getAttribute(attr) }));

  btnTranslate(words, lang, data => {
    els.forEach((el, i) => { if (data[i]) el.innerText = btnFix(data[i]); });
  });
}

function restoreEls(selector, attr) {
  document.querySelectorAll(selector).forEach(el => {
    const original = el.getAttribute(attr);
    if (original) el.innerText = original;
  });
}

// =============================================================================
// Sale label — save / translate / restore
// =============================================================================

function labelTranslateAll(lang) {
  document.querySelectorAll(LABEL_SELECTOR).forEach(el => {
    if (!el.hasAttribute('data-label-original')) {
      const text = labelGetText(el);
      if (text) el.setAttribute('data-label-original', text);
    }
  });

  const els   = Array.from(document.querySelectorAll(LABEL_SELECTOR)).filter(el => el.hasAttribute('data-label-original'));
  const words = els.map(el => ({ t: 1, w: el.getAttribute('data-label-original') }));

  btnTranslate(words, lang, data => {
    els.forEach((el, i) => { if (data[i]) labelSetText(el, btnFix(data[i])); });
  });
}

function labelRestoreAll() {
  document.querySelectorAll(LABEL_SELECTOR).forEach(el => {
    const original = el.getAttribute('data-label-original');
    if (original) labelSetText(el, original);
  });
}

// =============================================================================
// Combined translate / restore all targets
// =============================================================================


// =============================================================================
// Tooltip attribute — save / translate / restore
// =============================================================================

function tooltipTranslateAll(lang) {
  document.querySelectorAll(TOOLTIP_SELECTOR).forEach(el => {
    if (!el.hasAttribute('data-tooltip-original')) {
      const text = el.getAttribute('data-tooltip');
      if (text) el.setAttribute('data-tooltip-original', text);
    }
  });

  const els   = Array.from(document.querySelectorAll(TOOLTIP_SELECTOR)).filter(el => el.hasAttribute('data-tooltip-original'));
  const words = els.map(el => ({ t: 1, w: el.getAttribute('data-tooltip-original') }));

  btnTranslate(words, lang, data => {
    els.forEach((el, i) => { if (data[i]) el.setAttribute('data-tooltip', btnFix(data[i])); });
  });
}

function tooltipRestoreAll() {
  document.querySelectorAll(TOOLTIP_SELECTOR).forEach(el => {
    const original = el.getAttribute('data-tooltip-original');
    if (original) el.setAttribute('data-tooltip', original);
  });
}

function translateAll(lang) {
  translateEls(BTN_SELECTOR,   'data-btn-original',   lang);
  translateEls(INFO_SELECTOR,  'data-info-original',  lang);
  translateEls(TITLE_SELECTOR, 'data-title-original', lang);
  translateEls(LOCATION_SPAN_SELECTOR, 'data-loc-span-original', lang);
  translateEls(SWATCH_SELECTOR, 'data-swatch-original', lang);
  labelTranslateAll(lang);
  tooltipTranslateAll(lang);
}

function restoreAll() {
  restoreEls(BTN_SELECTOR,   'data-btn-original');
  restoreEls(INFO_SELECTOR,  'data-info-original');
  restoreEls(TITLE_SELECTOR, 'data-title-original');
  restoreEls(LOCATION_SPAN_SELECTOR, 'data-loc-span-original');
  restoreEls(SWATCH_SELECTOR, 'data-swatch-original');
  labelRestoreAll();
  tooltipRestoreAll();
}

// =============================================================================
// Handle new nodes — used by observer for single elements
// =============================================================================

function handleNode(node, lang) {
  if (!node.querySelectorAll) return;

  // Button spans
  node.querySelectorAll(BTN_SELECTOR).forEach(el => {
    saveOriginal(el, 'data-btn-original');
    if (btnIsSource(lang)) return;
    const text = el.getAttribute('data-btn-original');
    if (!text) return;
    btnTranslate([{ t: 1, w: text }], lang, data => {
      if (data[0]) el.innerText = btnFix(data[0]);
    });
  });

  // Info labels
  node.querySelectorAll(INFO_SELECTOR).forEach(el => {
    saveOriginal(el, 'data-info-original');
    if (btnIsSource(lang)) return;
    const text = el.getAttribute('data-info-original');
    if (!text) return;
    btnTranslate([{ t: 1, w: text }], lang, data => {
      if (data[0]) el.innerText = btnFix(data[0]);
    });
  });

  // Titles
  node.querySelectorAll(TITLE_SELECTOR).forEach(el => {
    saveOriginal(el, 'data-title-original');
    if (btnIsSource(lang)) return;
    const text = el.getAttribute('data-title-original');
    if (!text) return;
    btnTranslate([{ t: 1, w: text }], lang, data => {
      if (data[0]) el.innerText = btnFix(data[0]);
    });
  });

  // Tooltips
  node.querySelectorAll(TOOLTIP_SELECTOR).forEach(el => {
    if (!el.hasAttribute('data-tooltip-original')) {
      const text = el.getAttribute('data-tooltip');
      if (text) el.setAttribute('data-tooltip-original', text);
    }
    if (btnIsSource(lang)) return;
    const text = el.getAttribute('data-tooltip-original');
    if (!text) return;
    btnTranslate([{ t: 1, w: text }], lang, data => {
      if (data[0]) el.setAttribute('data-tooltip', btnFix(data[0]));
    });
  });

  // Sale labels
  node.querySelectorAll(LABEL_SELECTOR).forEach(el => {
    if (!el.hasAttribute('data-label-original')) {
      const text = labelGetText(el);
      if (text) el.setAttribute('data-label-original', text);
    }
    if (btnIsSource(lang)) return;
    const text = el.getAttribute('data-label-original');
    if (!text) return;
    btnTranslate([{ t: 1, w: text }], lang, data => {
      if (data[0]) labelSetText(el, btnFix(data[0]));
    });
  });

  // Location spans
  node.querySelectorAll(LOCATION_SPAN_SELECTOR).forEach(el => {
    saveOriginal(el, 'data-loc-span-original');
    if (btnIsSource(lang)) return;
    const text = el.getAttribute('data-loc-span-original');
    if (!text) return;
    btnTranslate([{ t: 1, w: text }], lang, data => {
      if (data[0]) el.innerText = btnFix(data[0]);
    });
  });

  // Swatch variant options
  node.querySelectorAll(SWATCH_SELECTOR).forEach(el => {
    saveOriginal(el, 'data-swatch-original');
    if (btnIsSource(lang)) return;
    const text = el.getAttribute('data-swatch-original');
    if (!text) return;
    btnTranslate([{ t: 1, w: text }], lang, data => {
      if (data[0]) el.innerText = btnFix(data[0]);
    });
  });
}

// =============================================================================
// Language change
// =============================================================================

function btnOnLangChange(newLang) {
  if (btnIsSource(newLang)) {
    restoreAll();
  } else {
    translateAll(newLang);
    setTimeout(() => translateAll(newLang), 100);
    setTimeout(() => translateAll(newLang), 300);
    setTimeout(() => translateAll(newLang), 700);
  }
}

// =============================================================================
// MutationObserver
// =============================================================================

function btnStartObserver() {
  new MutationObserver(mutations => {
    const lang = Weglot.getCurrentLang();
    if (btnIsSource(lang)) return;

    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;

          // CASE A — span directly inside a button (swatch click)
          if (node.closest?.('.tt-btn-addtocart')) {
            if (!node.innerText?.trim()) {
              setTimeout(() => handleNode(node.parentElement, lang), 50);
            } else {
              saveOriginal(node, 'data-btn-original');
              if (!btnIsSource(lang)) {
                const text = node.getAttribute('data-btn-original');
                if (text) btnTranslate([{ t: 1, w: text }], lang, data => {
                  if (data[0]) node.innerText = btnFix(data[0]);
                });
              }
            }
            return;
          }

          // CASE B — new product subtree (infinite scroll)
          if (!node.innerText?.trim()) {
            setTimeout(() => handleNode(node, lang), 50);
          } else {
            handleNode(node, lang);
          }
        });
      }

      // Text written into existing span (Slick initial render)
      if (mutation.type === 'characterData') {
        const parent = mutation.target.parentElement;
        if (parent?.matches?.(BTN_SELECTOR)) {
          saveOriginal(parent, 'data-btn-original');
          if (!btnIsSource(lang)) {
            const text = parent.getAttribute('data-btn-original');
            if (text) btnTranslate([{ t: 1, w: text }], lang, data => {
              if (data[0]) parent.innerText = btnFix(data[0]);
            });
          }
        }
      }
    });
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
}

// =============================================================================
// Swatch click fallback
// =============================================================================

function btnStartSwatchListener() {
  document.body.addEventListener('click', ({ target }) => {
    const swatch = target.closest('.tt-options-swatch li');
    if (!swatch) return;
    const lang = Weglot.getCurrentLang();
    setTimeout(() => {
      const span = swatch.closest('.tt-product')?.querySelector(BTN_SELECTOR);
      if (span) {
        saveOriginal(span, 'data-btn-original');
        if (!btnIsSource(lang)) {
          const text = span.getAttribute('data-btn-original');
          if (text) btnTranslate([{ t: 1, w: text }], lang, data => {
            if (data[0]) span.innerText = btnFix(data[0]);
          });
        }
      }
    }, 80);
  });
}

// =============================================================================
// Infinite scroll
// =============================================================================

function btnStartInfiniteScrollListener() {
  document.body.addEventListener('listingpageajax', () => {
    const lang = Weglot.getCurrentLang();
    if (btnIsSource(lang)) return;
    setTimeout(() => translateAll(lang), 100);
  });
}

// =============================================================================
// Boot
// =============================================================================

function btnWaitForWeglot(callback) {
  if (typeof Weglot !== 'undefined' && typeof Weglot.getCurrentLang === 'function') {
    callback(); return;
  }
  document.addEventListener('weglot:initialized', function onWeglotInit() {
    document.removeEventListener('weglot:initialized', onWeglotInit);
    callback();
  });
  const poll = setInterval(() => {
    if (typeof Weglot !== 'undefined' && typeof Weglot.getCurrentLang === 'function') {
      clearInterval(poll); callback();
    }
  }, 150);
}

document.addEventListener('DOMContentLoaded', () => {
  btnWaitForWeglot(() => {
    btnSourceLang = Weglot.options?.language_from ?? 'sr';
    
    // Check current language and translate if needed on initial load
    const currentLang = Weglot.getCurrentLang();
    if (!btnIsSource(currentLang)) {
      translateAll(currentLang);
      setTimeout(() => translateAll(currentLang), 100);
      setTimeout(() => translateAll(currentLang), 300);
    }
    
    Weglot.on('languageChanged', btnOnLangChange);
    btnStartObserver();
    btnStartSwatchListener();
    btnStartInfiniteScrollListener();
  });
});